import { xavis_experiments } from '../../y/debug';
import { $on, $trigger, YEventHandlers, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { $form, YInput, YInputOptions } from '../../y/form';
import { YFlags } from '../../y/types';
import { uiAnimate, uiScrollTo } from '../animate';
import { $el } from '../elements';
import { uiAddRipple } from '../ripple';
import { $svg, UISVG } from '../svg';
import { UIClassesSpec } from '../types';
import { UIView, UIViewAddItem, UIViewAddSpec } from '../view';
import { UIButton, UIButtonOptions } from './button';
import { UIInkBar } from './inkbar';

export type UIToggleGroup = UIAbstractToggle<unknown>[];

export interface UIAbstractToggle<Key = unknown> extends UIView, YInput<boolean> {
    key?: Key;
    group?: UIToggleGroup;
}

export interface UIToggleButtonOptions<Key = void> extends UIButtonOptions, YInputOptions {
    key?: Key;
    group?: UIToggleGroup;
    on_toggle_click?: (toggle: UIToggleButton<Key>) => void | boolean;
}

export interface UIToggleOptions<Key> extends YInputOptions {
    group?: UIToggleGroup;
    key?: Key;
    no_click_handler?: boolean;
    undefined_if_empty?: boolean;
}

export interface UICheckboxRadioOptions<Key> extends UIToggleOptions<Key> {
    title?: UIViewAddSpec;
    subtitle?: UIViewAddSpec;
    classes?: UIClassesSpec;
}

function changeGroup(toggle: UIAbstractToggle<unknown>, from?: UIToggleGroup, to?: UIToggleGroup) {
    if(from === to) return;

    if(from) {
        const i = from.indexOf(toggle);
        if(i >= 0)
            from.splice(i, 1);
    }

    if(to) {
        to.push(toggle);
    }
}

export class UIToggle<Key = void> extends UIView implements YInput<boolean> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<boolean>>;

    okay!: boolean;
    dirty!: boolean;
    key?: Key;

    private _value: boolean;
    private _group?: UIToggleGroup;
    private _required?: boolean;
    private _disabled!: boolean;
    private _undefined_if_empty?: boolean;

    constructor(element: HTMLElement, options: UIToggleOptions<Key> = {}) {
        super(element);
        this.key = options.key;

        this._value = false;
        this._required = options.required;
        this.disabled = !!options.disabled;
        this._undefined_if_empty = options.undefined_if_empty;

        if(options.group) {
            this._group = options.group;
            this._group.push(this);
        }

        $form.setOkay(this, !options.required);

        if(!options.no_click_handler) {
            this.root.addEventListener('click', e => {
                this.toggle();
                e.stopPropagation();
            });
        }
    }

    toggle() {
        if(this._disabled) return;

        $form.setDirty(this, true);
        if(this._group)
            this.value = true;
        else
            this.value = !this.value;
        $trigger(this, 'input');
    }

    get group() {
        return this._group;
    }

    set group(group: UIToggleGroup | undefined) {
        changeGroup(this, this._group, group);
        this._group = group;
    }

    get value(): boolean {
        if(this._undefined_if_empty && !this._value)
            return undefined as any;
        return this._value;
    }

    set value(v: boolean | undefined) {
        v = !!v;

        if(v === this._value) return;

        this._value = v;
        this.classList.toggle('f-checked', v);

        if(v && this._group) {
            for(const r of this._group) {
                if(r !== this && r.value)
                    r.value = false;
            }
        }

        $trigger(this, 'value', v);
        $form.setOkay(this, v || !this._required);
    }

    set disabled(disabled: boolean) {
        this._disabled = disabled;
        this.classList.toggle('f-disabled', disabled);
    }

    get disabled() {
        return this._disabled;
    }

    markInvalid(focus: boolean, _force?: boolean) {
        if(focus)
            this.focus();
        return focus;
    }

    focus() {
        uiScrollTo(this);
    }
}

abstract class UICheckboxRadioBase<Key> extends UIToggle<Key> {
    constructor(box: UIViewAddItem, options: UICheckboxRadioOptions<Key>) {
        options.no_click_handler = true;

        super($el.DIV(['ui-toggle i-input', options.classes], [
            box,
            options.title && $el.DIV('r-label', options.subtitle ? [$el.B(options.title), $el.DIV('r-subtitle', options.subtitle)] : options.title)
        ]), options);

        if(xavis_experiments)
            uiAddRipple(this, { fixed: true });

        this.root.addEventListener('click', e => {
            if(e.target !== this.root)
                this.toggle();
        });
    }
}

export class UICheckbox<Key = void> extends UICheckboxRadioBase<Key> {
    constructor(options: UICheckboxRadioOptions<Key> = {}) {
        const box = new UISVG('r-box', [24, 24]);

        box.add($svg.RECT(4, 4, 16, 16, 'r-frame'));
        box.add($svg.POLYLINE('7.7,10 12,14 21.6,5', 'r-tick'));

        super(box, options);
    }
}

export class UIRadio<Key = void> extends UICheckboxRadioBase<Key> {
    constructor(options: UICheckboxRadioOptions<Key>) {
        const box = new UISVG('r-box', [24, 24]);

        box.add($svg.CIRCLE(12, 12, 9, 'r-frame'));
        box.add($svg.LINE(12, 12, 12, 12, 'r-dot'));

        if(!options.group)
            options.group = [];

        super(box, options);
    }
}

export class UITickRadio<Key = void> extends UICheckboxRadioBase<Key> {
    constructor(options: UICheckboxRadioOptions<Key>) {
        const box = new UISVG('r-box', [24, 24]);

        box.add($svg.CIRCLE(12, 12, 9, 'r-fill-frame'));
        box.add($svg.CIRCLE(12, 12, 9, 'r-fill-circle'));
        box.add($svg.POLYLINE('7.1,11.3 10.7,14.7 17.6,7.7', 'r-fill-tick'));

        if(!options.group)
            options.group = [];

        super(box, options);
    }
}

export class UITickCheckbox<Key = void> extends UICheckboxRadioBase<Key> {
    constructor(options: UICheckboxRadioOptions<Key>) {
        const box = new UISVG('r-box', [24, 24]);

        box.add($svg.RECT(3, 3, 18, 18, 'r-fill-rect-frame'));
        box.add($svg.RECT(3, 3, 18, 18, 'r-fill-rect'));
        box.add($svg.POLYLINE('7.1,11.3 10.7,14.7 17.6,7.7', 'r-fill-tick'));

        super(box, options);
    }
}

interface UISwitchOptions<Key> extends UIToggleOptions<Key> {
    classes?: UIClassesSpec;
    on_label?: string;
    off_label?: string;
}

export class UISwitch<Key = void> extends UIToggle<Key> implements YInput<boolean> {
    constructor(options: UISwitchOptions<Key> = {}) {
        super($el.DIV(['ui-switch', options.classes], $el.DIV('r-thumb')), options);

        if(options.on_label || options.off_label) {
            this.classList.add('m-label');
            this.ui_add([$el.DIV('r-label r-left', options.on_label), $el.DIV('r-label r-right', options.off_label)]);
        }

        // TODO: add dragging back!!!!
    }
}

export class UIToggleButton<Key = void> extends UIButton implements YInput<boolean> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<boolean>>;

    okay!: boolean;
    dirty!: boolean;
    key?: Key;

    private _required?: boolean;
    private _group?: UIToggleGroup

    constructor(options: UIToggleButtonOptions<Key>) {
        super(options);

        this.key = options.key;
        this._required = options.required;

        const on_toggle_click = options.on_toggle_click;

        this.on_click = () => {
            if(on_toggle_click?.(this) === false) return;
            this.toggle();
        };

        if(options.group) {
            this._group = options.group;
            this._group.push(this);
        }

        $form.setOkay(this, !options.required);
    }

    toggle() {
        if(this.disabled) return;

        $form.setDirty(this, true);
        if(this._group)
            this.value = true;
        else
            this.value = !this.value;
        $trigger(this, 'input');
    }

    get value() {
        return this.checked;
    }

    set value(v: boolean) {
        if(v == this.checked) return;
        this.checked = v;

        if(v && this._group) {
            for(const r of this._group) {
                if(r !== this && r.value)
                    r.value = false;
            }
        }

        $trigger(this, 'value', v);
        $form.setOkay(this, v || !this._required);
    }

    get group() {
        return this._group;
    }

    set group(group: UIToggleGroup | undefined) {
        changeGroup(this, this._group, group);
        this._group = group;
    }

    markInvalid(focus: boolean, _force?: boolean) {
        if(focus)
            this.focus();
        return focus;
    }

    focus() {
        uiScrollTo(this);
    }
}

export interface UITogglesOptions extends YInputOptions {
    classes?: UIClassesSpec;
    inkbar?: UIInkBar;
}

abstract class UIToggles<Object, Toggle extends UIAbstractToggle<any> & YInput<boolean>, Value> extends UIView implements YInput<Value> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Value>>;
    abstract value: Value;

    okay!: boolean;
    dirty!: boolean;
    required?: boolean;

    private _generator: (value: Object) => Toggle;
    private _values!: Object[];
    protected _toggles: Toggle[];
    protected _lock?: boolean;
    private _disabled!: boolean;
    private _exclusive?: boolean;

    constructor(values: Object[], generator: (value: Object) => Toggle, options: UITogglesOptions = {}, exclusive?: boolean) {
        super('div', ['ui-toggles i-input', options.classes]);

        this._generator = generator;
        this._toggles = [];
        this.required = options.required;
        this._exclusive = exclusive;
        this.values = values;

        if(options.disabled)
            this.disabled = true;

        $form.setOkay(this, !options.required);
    }

    protected abstract onValueChanged(): void;

    reload() {
        const old_value = this.value;

        const toggles = this._toggles;
        const generator = this._generator;
        const values = this._values;

        if(toggles.length) {
            for(const t of toggles)
                t.remove();
            toggles.length = 0;
        }

        const group = this._exclusive ? [] : undefined;

        for(const v of values) {
            let toggle = generator(v);
            toggle.group = group;
            toggles.push(toggle);
            this.ui_add(toggle);
            $on(toggle, 'value', v => {
                if(group && !v) return;
                $form.setDirty(this, true);
                this.classList.remove('f-invalid');
                if(!this._lock)
                    this.onValueChanged();
            });
        }

        this.value = old_value;
    }

    set values(values: Object[]) {
        this._values = values;
        this.reload();
    }

    get values() {
        return this._values;
    }

    get disabled() {
        return this._disabled;
    }

    set disabled(disabled: boolean) {
        this._disabled = disabled;
        for(const t of this._toggles) {
            t.disabled = disabled;
        }
    }

    markInvalid(focus: boolean, force?: boolean): boolean {
        if(this.okay && !force) return false;
        uiAnimate([{ what: this, animation: 'flash-invalid', duration: 1.5 }]);
        if(focus) this.focus();
        this.classList.add('f-invalid');
        return focus;
    }

    focus() {
        uiScrollTo(this);
    }
}

export class UIMultiToggles<Object, Key> extends UIToggles<Object, UIAbstractToggle<Key>, Key[]> {
    constructor(values: Object[], generator: (value: Object) => UIAbstractToggle<Key>, options: UITogglesOptions = {}) {
        super(values, generator, options);
    }

    protected onValueChanged() {
        const v = this.value;
        $form.setOkay(this, !this.required || !!v.length);
        $trigger(this, 'value', v);
    }

    get value(): Key[] {
        const ret: Key[] = [];
        for(const t of this._toggles) {
            if(t.value && t.key)
                ret.push(t.key);
        }
        return ret;
    }

    set value(v: (Key | undefined)[] | undefined) {
        this._lock = true;
        for(const t of this._toggles) {
            t.value = (t.key && v && v.includes(t.key)) || false;
        }
        this._lock = false;
        this.onValueChanged();
    }

    checkAll() {
        this.value = this._toggles.map(t => t.key);
    }
}

export class UIFlagsToggles<Object, Key extends string> extends UIToggles<Object, UIAbstractToggle<Key>, YFlags<Key>> {
    constructor(values: Object[], generator: (value: Object) => UIAbstractToggle<Key>, options: UITogglesOptions = {}) {
        super(values, generator, options);
    }

    protected onValueChanged() {
        const v = this.value;
        $form.setOkay(this, !this.required || !!Object.keys(v).length);
        $trigger(this, 'value', v);
    }

    get value(): YFlags<Key> {
        const ret: YFlags<Key> = {};
        for(const t of this._toggles) {
            if(t.value && t.key)
                ret[t.key] = true;
        }
        return ret;
    }

    set value(v: YFlags<Key> | undefined) {
        this._lock = true;
        for(const t of this._toggles) {
            t.value = (t.key && v && v[t.key]) || false;
        }
        this._lock = false;
        this.onValueChanged();
    }

    checkAll() {
        this.value = Object.fromEntries(this._toggles.map(t => [t.key, true]));
    }
}

export class UIExclusiveToggles<Object, Key> extends UIToggles<Object, UIAbstractToggle<Key>, Key | undefined> {
    private _inkbar?: UIInkBar

    constructor(values: Object[], generator: (value: Object) => UIAbstractToggle<Key>, options: UITogglesOptions = {}) {
        super(values, generator, options, true);

        this._inkbar = options.inkbar;
    }

    protected onValueChanged() {
        const v = this.value;
        $form.setOkay(this, !this.required || !!v);
        $trigger(this, 'value', v);

        if(this._inkbar) {
            for(const t of this._toggles) {
                if(t.value) {
                    this._inkbar.update(t);
                    return;
                }
            }
        }
    }

    get selected_index(): number {
        for(let i = 0; i < this._toggles.length; i++) {
            if(this._toggles[i].value)
                return i;
        }
        return -1;
    }

    get value(): Key | undefined {
        for(const t of this._toggles) {
            if(t.value)
                return t.key;
        }
        return;
    }

    set value(v: Key | undefined) {
        this._lock = true;
        for(const t of this._toggles) {
            t.value = t.key === v;
        }
        this._lock = false;
        this.onValueChanged();
    }
}
